import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Table from '../../components/Table';
import api from '../../services/api';
import '../../styles/components.css';

const EnrollScholar = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'bulk'
    const [supervisors, setSupervisors] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);

    // Form data for manual enrollment
    const [formData, setFormData] = useState({
        scholarName: '',
        tceRollNo: '',
        department: '',
        supervisorName: '',
        trfAdmittedDate: '',
        fellowshipStatus: 'pursuing',
        phdStatus: 'pursuing',
        email: '',
        phone: '',
        balCL: '',
        balCLExpire: ''
    });

    useEffect(() => {
        fetchSupervisors();
        fetchDepartments();
    }, []);

    const fetchSupervisors = async () => {
        try {
            const response = await api.get('/admin/supervisors');
            setSupervisors(response.data);
        } catch (err) {
            console.error('Error fetching supervisors:', err);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await api.get('/admin/departments');
            setDepartments(response.data);
        } catch (err) {
            console.error('Error fetching departments:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            // If department changes, clear the supervisor selection
            if (name === 'department') {
                return {
                    ...prev,
                    [name]: value,
                    supervisorName: '' // Clear supervisor when department changes
                };
            }
            return {
                ...prev,
                [name]: value
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Format TRF Admitted Date to "Month, Year"
        let formattedDate = '';
        if (formData.trfAdmittedDate) {
            const [year, month] = formData.trfAdmittedDate.split('-');
            const date = new Date(year, month - 1);
            const monthName = date.toLocaleString('default', { month: 'long' });
            formattedDate = `${monthName}, ${year}`;
        }

        const submitData = {
            ...formData,
            trfAdmittedDate: formattedDate
        };

        try {
            await api.post('/admin/scholars', submitData);
            setSuccess('Scholar enrolled successfully!');
            setFormData({
                scholarName: '',
                tceRollNo: '',
                department: '',
                supervisorName: '',
                trfAdmittedDate: '',
                fellowshipStatus: 'pursuing',
                phdStatus: 'pursuing',
                email: '',
                phone: '',
                balCL: '',
                balCLExpire: ''
            });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to enroll scholar');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        setError('');
        setSuccess('');
        setBulkUploadResult(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post('/admin/scholars/bulk-upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setSuccess(response.data.message);
            setBulkUploadResult(response.data);
        } catch (err) {
            console.error('Bulk upload error:', err);
            setError(err.response?.data?.error || 'Failed to upload file');
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const downloadTemplate = () => {
        const headers = [
            'Scholar Name',
            'TCE Roll No',
            'Email',
            'Phone',
            'Dept',
            'Supervisor',
            'TRF Admitted Month & Year (Format: March,2026)',
            'Fellowship Status',
            'Ph.D. Status',
            'Balance CL',
            'Balance CL Valid To Date (DD-MM-YYYY)'
        ];

        const sampleData = [
            'John Doe',
            'PHD2024001',
            'john.doe@tce.edu',
            '9876543210',
            'Computer Science',
            'Dr. Smith',
            'January, 2024',
            'pursuing',
            'pursuing',
            '10',
            '01-12-2027'
        ];

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, sampleData]);
        XLSX.utils.book_append_sheet(wb, ws, "Scholar Template");

        // Generate and download file
        XLSX.writeFile(wb, 'scholar_upload_template.xlsx');
    };

    const failedColumns = [
        { header: 'Row', field: 'row' },
        { header: 'Error', field: 'error' }
    ];

    const successColumns = [
        { header: 'Row', field: 'row' },
        { header: 'Scholar Name', field: 'scholarName' },
        { header: 'TCE Roll No', field: 'tceRollNo' }
    ];

    return (
        <Layout title="Enroll Scholar">
            {/* Tab Navigation */}
            <div className="tabs" style={{ marginBottom: '1.5rem' }}>
                <button
                    className={`tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
                    onClick={() => setActiveTab('manual')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: activeTab === 'manual' ? '#3b82f6' : '#e5e7eb',
                        color: activeTab === 'manual' ? 'white' : '#374151',
                        cursor: 'pointer',
                        borderRadius: '0.5rem 0 0 0.5rem',
                        fontWeight: '500'
                    }}
                >
                    Manual Enrollment
                </button>
                <button
                    className={`tab-btn ${activeTab === 'bulk' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bulk')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: activeTab === 'bulk' ? '#3b82f6' : '#e5e7eb',
                        color: activeTab === 'bulk' ? 'white' : '#374151',
                        cursor: 'pointer',
                        borderRadius: '0 0.5rem 0.5rem 0',
                        fontWeight: '500'
                    }}
                >
                    Bulk Upload (Excel)
                </button>
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}

            {/* Manual Enrollment Form */}
            {activeTab === 'manual' && (
                <Card title="Scholar Enrollment Form">
                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                            <div className="form-group">
                                <label className="form-label">Scholar Name *</label>
                                <input
                                    type="text"
                                    name="scholarName"
                                    className="form-input"
                                    value={formData.scholarName}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter scholar's full name"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">TCE Roll No *</label>
                                <input
                                    type="text"
                                    name="tceRollNo"
                                    className="form-input"
                                    value={formData.tceRollNo}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g., PHD2024001"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Department *</label>
                                <select
                                    name="department"
                                    className="form-select"
                                    value={formData.department}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Department</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Supervisor *</label>
                                <select
                                    name="supervisorName"
                                    className="form-select"
                                    value={formData.supervisorName}
                                    onChange={handleChange}
                                    required
                                    disabled={!formData.department}
                                >
                                    <option value="">
                                        {formData.department ? 'Select Supervisor' : 'Select Department First'}
                                    </option>
                                    {supervisors
                                        .filter(sup => !formData.department || sup.department === formData.department)
                                        .map(sup => (
                                            <option key={sup.id} value={sup.fullName}>
                                                {sup.fullName}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">TRF Admitted Month & Year *</label>
                                <input
                                    type="month"
                                    name="trfAdmittedDate"
                                    className="form-input"
                                    value={formData.trfAdmittedDate}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Fellowship Status *</label>
                                <select
                                    name="fellowshipStatus"
                                    className="form-select"
                                    value={formData.fellowshipStatus}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="pursuing">Pursuing</option>
                                    <option value="completed">Completed</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="terminated">Terminated</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Ph.D. Status *</label>
                                <select
                                    name="phdStatus"
                                    className="form-select"
                                    value={formData.phdStatus}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="pursuing">Pursuing</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Balance CL *</label>
                                <input
                                    type="number"
                                    name="balCL"
                                    className="form-input"
                                    value={formData.balCL}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    placeholder="Enter current CL balance"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Balance CL Valid To Date *</label>
                                <input
                                    type="date"
                                    name="balCLExpire"
                                    className="form-input"
                                    value={formData.balCLExpire}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Email * <small style={{ color: '#6b7280', fontWeight: 'normal' }}>(Used for login)</small></label>
                                <input
                                    type="email"
                                    name="email"
                                    className="form-input"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="scholar@example.com"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Phone *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    className="form-input"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    placeholder="+91 1234567890"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Enrolling...' : 'Enroll Scholar'}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => navigate('/admin')}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            {/* Bulk Upload Section */}
            {activeTab === 'bulk' && (
                <Card title="Bulk Upload Scholars">
                    <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ marginBottom: '0.5rem' }}>Instructions:</h4>
                        <ol style={{ paddingLeft: '1.5rem', color: '#6b7280' }}>
                            <li>Download the template file below</li>
                            <li>Fill in the scholar details in the spreadsheet. <b>Note: Department and Supervisor names must match exactly with system records.</b></li>
                            <li>Use <b>DD-MM-YYYY</b> format for <b>Balance CL Valid To Date</b> (e.g., 01-12-2027).</li>
                            <li>Use <b>Month, Year</b> format for <b>TRF Admitted Month & Year</b> (e.g., March,2026).</li>
                            <li>Save the file as Excel (.xlsx)</li>
                            <li>Upload the file using the button below</li>
                        </ol>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={downloadTemplate}>
                            📥 Download Template (XLSX)
                        </Button>

                        <div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleBulkUpload}
                                accept=".xlsx"
                                style={{ display: 'none' }}
                                id="bulk-upload-input"
                            />
                            <Button
                                variant="primary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={loading}
                            >
                                {loading ? 'Uploading...' : '📤 Upload File'}
                            </Button>
                        </div>
                    </div>

                    <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ marginBottom: '0.5rem' }}>Required Columns:</h4>
                        <code style={{ fontSize: '0.875rem', color: '#374151', display: 'block', wordWrap: 'break-word' }}>
                            Scholar Name, TCE Roll No, Email, Phone, Dept, Supervisor, TRF Admitted Month & Year (Format: March,2026), Fellowship Status, Ph.D. Status, Balance CL, Balance CL Valid To Date
                        </code>
                        <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#dc2626' }}>
                            * All columns are mandatory for enrollment.
                        </p>
                    </div>

                    {/* Upload Results */}
                    {bulkUploadResult && (
                        <div style={{ marginTop: '2rem' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '1rem',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{ background: '#dbeafe', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e40af' }}>
                                        {bulkUploadResult.totalProcessed}
                                    </div>
                                    <div style={{ color: '#3b82f6' }}>Total Processed</div>
                                </div>
                                <div style={{ background: '#dcfce7', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#166534' }}>
                                        {bulkUploadResult.successCount}
                                    </div>
                                    <div style={{ color: '#22c55e' }}>Successful</div>
                                </div>
                                <div style={{ background: '#fee2e2', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#991b1b' }}>
                                        {bulkUploadResult.failedCount}
                                    </div>
                                    <div style={{ color: '#ef4444' }}>Failed</div>
                                </div>
                            </div>

                            {bulkUploadResult.success.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h4 style={{ marginBottom: '0.5rem', color: '#166534' }}>✓ Successfully Enrolled:</h4>
                                    <Table
                                        columns={successColumns}
                                        data={bulkUploadResult.success}
                                        emptyMessage="No successful enrollments"
                                    />
                                </div>
                            )}

                            {bulkUploadResult.failed.length > 0 && (
                                <div>
                                    <h4 style={{ marginBottom: '0.5rem', color: '#991b1b' }}>✗ Failed Enrollments:</h4>
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <Table
                                            columns={failedColumns}
                                            data={bulkUploadResult.failed}
                                            emptyMessage="No failed enrollments"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ marginTop: '2rem' }}>
                        <Button
                            variant="secondary"
                            onClick={() => navigate('/admin')}
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                </Card>
            )}
        </Layout>
    );
};

export default EnrollScholar;
